import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Drawer, Form, Input, InputNumber, Select, DatePicker, Space, Card,
  message, Typography, Modal, Switch, Divider,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useSidebar } from '../../contexts/SidebarContext';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../../components/shared/PageHeader';
import {
  listGroupInstances, getGroupInstance, createGroupInstance, updateGroupInstance, deleteGroupInstance,
  addGroupMember, removeGroupMember, resolveGroupPolicy,
  listPackages, listControlTypes, listNfsLocations, listGroupTypes,
  GroupInstance, GroupMember, WorkspacePackage, ControlType, NfsLocation, GroupType,
} from '../../services/workspaceApi';

const errMsg = (e: unknown, fallback: string) => {
  const err = e as { errorFields?: unknown; response?: { data?: { message?: string } } };
  if (err?.errorFields) return null;
  return err?.response?.data?.message || fallback;
};

const GroupInstancesPage: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const role = useSelector((s: RootState) => s.auth.user?.role);
  const canWrite = role === 'itadmin' || role === 'superadmin';

  const [rows, setRows] = useState<GroupInstance[]>([]);
  const [packages, setPackages] = useState<WorkspacePackage[]>([]);
  const [controlTypes, setControlTypes] = useState<ControlType[]>([]);
  const [groupTypes, setGroupTypes] = useState<GroupType[]>([]);
  const [nfsLocations, setNfsLocations] = useState<NfsLocation[]>([]);
  const [loading, setLoading] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<GroupInstance | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [form] = Form.useForm();
  const [memberForm] = Form.useForm();
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  const loadLookups = useCallback(async () => {
    try {
      const [pkg, ct, nfs, gt] = await Promise.all([
        listPackages(), listControlTypes(), listNfsLocations(), listGroupTypes(),
      ]);
      setPackages(Array.isArray(pkg) ? pkg : []);
      setControlTypes(Array.isArray(ct) ? ct : []);
      setNfsLocations(Array.isArray(nfs) ? nfs : []);
      setGroupTypes(Array.isArray(gt) ? gt : []);
    } catch {
      /* lookups optional for list view */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listGroupInstances();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      message.error('Failed to load group instances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadLookups();
  }, [load, loadLookups]);

  const nfsByRole = (roleName: string) =>
    nfsLocations.filter((n) => n.role === roleName).map((n) => ({ value: n.id, label: `${n.code} (${n.mountPath})` }));

  const openCreate = () => {
    setEditing(null);
    setMembers([]);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEdit = async (record: GroupInstance) => {
    try {
      const full = await getGroupInstance(record.id);
      setEditing(full);
      setMembers(full.members || []);
      form.setFieldsValue({
        ...full,
        startDate: full.startDate ? dayjs(full.startDate) : null,
        endDate: full.endDate ? dayjs(full.endDate) : null,
      });
      setDrawerOpen(true);
    } catch (e) {
      message.error(errMsg(e, 'Failed to load group') || 'Failed to load group');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const affinityRaw = values.affinityServerIds;
      const affinityIds =
        typeof affinityRaw === 'string'
          ? affinityRaw.split(',').map((s: string) => parseInt(s.trim(), 10)).filter((n: number) => !Number.isNaN(n))
          : Array.isArray(affinityRaw)
            ? affinityRaw
            : undefined;
      const payload: Partial<GroupInstance> & Record<string, unknown> = {
        ...values,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
      };
      if (affinityIds) {
        payload.affinityServerIds = affinityIds as any;
      }
      if (values.scheduledHours != null && typeof values.scheduledHours === 'number') {
        payload.scheduledHours = { weekly: values.scheduledHours } as any;
      }
      if (editing) {
        await updateGroupInstance(editing.id, payload);
        message.success('Updated');
      } else {
        await createGroupInstance(payload);
        message.success('Created');
      }
      setDrawerOpen(false);
      form.resetFields();
      await load();
    } catch (e) {
      const m = errMsg(e, 'Save failed');
      if (m) message.error(m);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record: GroupInstance) => {
    Modal.confirm({
      title: 'Delete group instance?',
      content: `Remove group #${record.id}${record.name ? ` (${record.name})` : ''}?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteGroupInstance(record.id);
          message.success('Deleted');
          await load();
        } catch (e) {
          message.error(errMsg(e, 'Delete failed') || 'Delete failed');
        }
      },
    });
  };

  const handleResolve = async (record: GroupInstance) => {
    setResolving(true);
    try {
      const result = await resolveGroupPolicy(record.id);
      Modal.info({
        title: 'Resolved Policy',
        width: 640,
        content: (
          <pre className="text-xs overflow-auto max-h-96 bg-gray-50 p-2 rounded">
            {JSON.stringify(result, null, 2)}
          </pre>
        ),
      });
    } catch (e) {
      message.error(errMsg(e, 'Resolve policy failed') || 'Resolve policy failed');
    } finally {
      setResolving(false);
    }
  };

  const handleAddMember = async () => {
    if (!editing) return;
    try {
      const values = await memberForm.validateFields();
      setAddingMember(true);
      const created = await addGroupMember(editing.id, values);
      setMembers((prev) => [...prev, created]);
      message.success('Member added');
      setMemberModalOpen(false);
      memberForm.resetFields();
    } catch (e) {
      const m = errMsg(e, 'Add member failed');
      if (m) message.error(m);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = (member: GroupMember) => {
    if (!editing || member.id == null) return;
    Modal.confirm({
      title: 'Remove member?',
      content: `Remove ${member.userId}?`,
      okType: 'danger',
      onOk: async () => {
        try {
          await removeGroupMember(editing.id, member.id!);
          setMembers((prev) => prev.filter((m) => m.id !== member.id));
          message.success('Removed');
        } catch (e) {
          message.error(errMsg(e, 'Remove failed') || 'Remove failed');
        }
      },
    });
  };

  const pkgLabel = (id?: number | null) => {
    if (id == null) return '—';
    const p = packages.find((x) => x.id === id);
    return p ? `${p.code}` : String(id);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Code', dataIndex: 'code', key: 'code', width: 100 },
    {
      title: 'Package',
      dataIndex: 'packageId',
      key: 'packageId',
      width: 120,
      render: (id: number) => pkgLabel(id),
    },
    {
      title: 'Start',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 110,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: 'End',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 110,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    { title: 'Profile', dataIndex: 'profileId', key: 'profileId', width: 120, ellipsis: true },
    {
      title: 'Actions',
      key: 'actions',
      width: canWrite ? 260 : 100,
      render: (_: unknown, record: GroupInstance) => (
        <Space wrap>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            {canWrite ? 'Edit' : 'View'}
          </Button>
          {canWrite && (
            <>
              <Button type="link" size="small" icon={<ThunderboltOutlined />} loading={resolving} onClick={() => handleResolve(record)}>
                Resolve Policy
              </Button>
              <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
                Delete
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const memberCols = [
    { title: 'User ID', dataIndex: 'userId', key: 'userId' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Primary',
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      width: 80,
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
    canWrite && editing
      ? {
          title: '',
          key: 'rm',
          width: 80,
          render: (_: unknown, record: GroupMember) => (
            <Button type="link" danger size="small" onClick={() => handleRemoveMember(record)}>
              Remove
            </Button>
          ),
        }
      : null,
  ].filter(Boolean);

  return (
    <div className={`p-4 ${isSidebarOpen ? 'ml-64' : 'ml-16'} transition-all`}>
      <Card className="shadow">
        {useNew ? (
          <PageHeader
            title="Group Instances"
            purpose="Workspace group cohorts: package, control, NFS mounts, affinity, and members."
            actions={
              canWrite ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  New Group
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="flex justify-between items-center mb-4">
            <Typography.Title level={4} className="!mb-0">
              Group Instances
            </Typography.Title>
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                New Group
              </Button>
            )}
          </div>
        )}

        <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={rows} pagination={{ pageSize: 20 }} />
      </Card>

      <Drawer
        title={editing ? `Group #${editing.id}` : 'New Group Instance'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={560}
        destroyOnClose
        extra={
          canWrite ? (
            <Space>
              <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
              <Button type="primary" loading={saving} onClick={handleSave}>
                Save
              </Button>
            </Space>
          ) : (
            <Button onClick={() => setDrawerOpen(false)}>Close</Button>
          )
        }
      >
        <Form form={form} layout="vertical" disabled={!canWrite}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="groupTypeId" label="Group Type" rules={[{ required: true, message: 'Required' }]}>
            <Select
              options={groupTypes.map((g) => ({
                value: g.id,
                label: `${g.kind}/${g.code} — ${g.displayName}`,
              }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Space className="w-full" size="middle">
            <Form.Item name="startDate" label="Start" className="flex-1">
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item name="endDate" label="End" className="flex-1">
              <DatePicker className="w-full" />
            </Form.Item>
          </Space>
          <Form.Item name="packageId" label="Package">
            <Select
              allowClear
              options={packages.map((p) => ({ value: p.id, label: `${p.code} — ${p.displayName}` }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="controlTypeId" label="Control Type">
            <Select
              allowClear
              options={controlTypes.map((c) => ({ value: c.id, label: `${c.code} — ${c.displayName}` }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="toolsNfsLocationId" label="Tools NFS">
            <Select allowClear options={nfsByRole('tools')} />
          </Form.Item>
          <Form.Item name="labNfsLocationId" label="Labs NFS">
            <Select allowClear options={nfsByRole('labs')} />
          </Form.Item>
          <Form.Item name="pdkNfsLocationId" label="PDK NFS">
            <Select allowClear options={nfsByRole('pdk')} />
          </Form.Item>
          <Form.Item
            name="affinityServerIds"
            label="Affinity (server IDs)"
            extra="Comma-separated infra server IDs"
          >
            <Input placeholder="e.g. 1,2,5" />
          </Form.Item>
          <Space className="w-full" size="middle">
            <Form.Item name="scheduledHours" label="Scheduled Hours" className="flex-1">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="maxWeekHours" label="Max Week Hours" className="flex-1">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="profileId" label="Profile ID">
            <Input placeholder="IWM profile id" />
          </Form.Item>
        </Form>

        {editing && (
          <>
            <Divider>Members</Divider>
            {canWrite && (
              <div className="mb-2 flex justify-end">
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    memberForm.resetFields();
                    memberForm.setFieldsValue({ isPrimary: false });
                    setMemberModalOpen(true);
                  }}
                >
                  Add Member
                </Button>
              </div>
            )}
            <Table
              rowKey={(r) => String(r.id ?? r.userId)}
              size="small"
              pagination={false}
              columns={memberCols as any}
              dataSource={members}
            />
          </>
        )}
      </Drawer>

      <Modal
        title="Add Member"
        open={memberModalOpen}
        onOk={handleAddMember}
        confirmLoading={addingMember}
        onCancel={() => setMemberModalOpen(false)}
        okText="Add"
        destroyOnClose
      >
        <Form form={memberForm} layout="vertical">
          <Form.Item name="userId" label="User ID" rules={[{ required: true, message: 'Required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="isPrimary" label="Primary" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GroupInstancesPage;
